import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

import { fmtMoney } from "../bexio/bexioFormat";
import { selectCostStructure } from "./financeSelectors";

/**
 * "Kostenstruktur" — the selected period's operating costs, split by bucket.
 *
 * P3c: the A2 contract folds the same per-month truth over a window, so a range
 * splits the RANGE's costs by bucket. Nothing is multiplied or re-bucketed here;
 * the card reads whatever `operating_costs` the service answered the selected
 * period with, exactly as it always read the month's.
 *
 * MOVED FROM THE ÜBERSICHT TAB IN F2c, not copied. The Übersicht is the compact
 * management summary; the split by bucket is analysis and belongs beside the
 * receipts chart and the per-unit ranking that read the same A2 contract. Two
 * cards rendering these five categories on two tabs would be one number with two
 * homes — and the first time one of them was "fixed" they would disagree. So
 * this component has exactly one consumer, and `companyFinanceContract.test.js`
 * keeps it that way.
 *
 * NO SECOND REQUEST. The A2 breakdown is already loaded for the "Betriebskosten" KPI
 * card; this card receives the same `{ data, error }` object and reads it again.
 *
 * THE RING IS OPERATING COSTS ONLY.
 * `unclassified` is a cost row whose `cost_type` A2a did not recognise, and it is
 * shown BELOW the ring, never inside it. Folding it in would make this card agree with
 * the legacy `summary.total_costs` — which sums every row without classifying any of
 * them, and is precisely the figure this whole surface exists to stop reporting. The
 * ring, its centre total and the KPI card therefore all state the same quantity.
 *
 * The centre prints the contract's own `total_chf`, not a sum of the five slices: A2
 * rounds every figure independently, so adding the rounded parts can miss the true
 * total by a centime.
 */

const CARD = "flex h-full flex-col rounded-xl border border-border bg-card p-4 shadow-sm";

const fmtShare = (share) =>
  share === null
    ? null
    : `${share.toLocaleString("de-CH", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })} %`;

function CardShell({ children, subtitle }) {
  return (
    <section aria-labelledby="finance-costs-heading" className={CARD}>
      <h2
        id="finance-costs-heading"
        className="text-[13px] font-semibold text-card-foreground"
      >
        Kostenstruktur
      </h2>
      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
        {subtitle}
      </p>
      {children}
    </section>
  );
}

export default function FinanceCostStructureCard({
  costBreakdown,
  window: periodWindow,
  loading = false,
  onRetry,
}) {
  const error = costBreakdown?.error;
  const isRange = Boolean(periodWindow?.isRange);
  const subtitle = isRange
    ? "Betriebskosten im gewählten Zeitraum"
    : "Betriebskosten im ausgewählten Monat";
  const noData = isRange
    ? "Für diesen Zeitraum liegen keine Kostendaten vor."
    : "Für diesen Monat liegen keine Kostendaten vor.";
  const noCosts = isRange
    ? "Für diesen Zeitraum sind keine Betriebskosten erfasst."
    : "Für diesen Monat sind keine Betriebskosten erfasst.";

  if (loading) {
    return (
      <CardShell subtitle={subtitle}>
        <div className="mt-3 space-y-2" aria-busy="true">
          <Skeleton className="h-[120px] w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </CardShell>
    );
  }

  if (error) {
    return (
      <CardShell subtitle={subtitle}>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <p role="alert" className="text-[12px] text-destructive">
            Kostenstruktur konnte nicht geladen werden.
          </p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            >
              Erneut laden
            </button>
          ) : null}
        </div>
      </CardShell>
    );
  }

  const { total, raw, segments, hasAmount, unclassified } =
    selectCostStructure(costBreakdown?.data);

  return (
    <CardShell subtitle={subtitle}>
      {hasAmount ? (
        <div className="relative mx-auto mt-3 h-[132px] w-[132px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={segments}
                dataKey="value"
                nameKey="name"
                innerRadius={42}
                outerRadius={62}
                paddingAngle={1}
                stroke="none"
                isAnimationActive={false}
              >
                {segments.map((s) => (
                  <Cell key={s.key} fill={s.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[10px] text-muted-foreground">Total</span>
            <span className="text-[13px] font-semibold text-card-foreground">
              {fmtMoney(raw.total)}
            </span>
          </div>
        </div>
      ) : (
        <p className="mt-4 text-[12px] text-muted-foreground">
          {total === null ? noData : noCosts}
        </p>
      )}

      {/* Every bucket is listed with its amount, whether or not the ring shows a
          slice for it: a zero bucket is a statement about the portfolio, and the list
          is also what makes the split readable without perceiving colour at all. */}
      <ul className="mt-3 list-none space-y-1.5 p-0 text-[11px]">
        {segments.map((s) => {
          const share = fmtShare(s.share);
          return (
            <li key={s.key} className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2">
                <span
                  aria-hidden="true"
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: s.color }}
                />
                <span className="truncate text-muted-foreground">{s.name}</span>
              </span>
              <span className="shrink-0 text-right font-medium text-card-foreground">
                {fmtMoney(s.raw)}
                {share ? (
                  <span className="ml-1 font-normal text-muted-foreground">({share})</span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Outside the ring and outside the total, by contract. Absent entirely when
          every cost row classified — nothing is wrong, so nothing is said. */}
      {unclassified.present ? (
        <div className="mt-3 border-t border-border pt-2">
          <div className="flex items-center justify-between gap-3 text-[11px]">
            <span className="text-muted-foreground">Nicht klassifiziert</span>
            <span className="font-medium text-card-foreground">
              {fmtMoney(unclassified.raw)}
            </span>
          </div>
          <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
            {unclassified.count === 1
              ? "1 Kostenposition ohne erkannten Typ — nicht in den Betriebskosten enthalten."
              : `${unclassified.count} Kostenpositionen ohne erkannten Typ — nicht in den Betriebskosten enthalten.`}
          </p>
        </div>
      ) : null}
    </CardShell>
  );
}
